/**
 * @fileoverview Instructor onboarding: one question, then you're teaching.
 *
 * Four steps became one (B3 of the iOS publication review, applied to both
 * clients so they ask the same things). The page welcomes the instructor by
 * the name sign-up already has and asks what they teach: chips for the
 * common instruments, with the tag field beside them for anything else.
 * Nothing is required; the same question waits in Settings › Profile with
 * the studio details that used to be a step here. Recording consent is
 * explained where the lock bites, on the first Record, not before there is a
 * student to record.
 *
 * A Founding Ensemble member still answers how many students they teach,
 * and their rail continues from enrolment. Finishing lands on the invitation
 * that brought them, when one is waiting, else on the dashboard with the
 * welcome.
 */

import { useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import TagInput from "@/components/TagInput";
import { INSTRUMENTS } from "@/lib/instruments";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { isFoundingMember, STUDENT_BANDS } from "@/lib/founding";
import { consumeInvite, inviteDestination, peekInvite } from "@/lib/pendingInvite";
// Shared with the founding enrolment flow so the two rails cannot drift.
import { fadeUp, FOUNDING_STEPS, JOURNEY_STEPS } from "@/lib/onboardingMotion";

const BAND_LABELS = { "1-10": "1–10", "11-25": "11–25", "26-50": "26–50", "50+": "50+" };

/**
 * The chips: the instruments most studios teach, in the canonical list's own
 * spelling, so chips and the typed field never disagree about a name. The
 * same list as the iOS screen.
 */
const COMMON_INSTRUMENTS = ["Piano", "Guitar", "Voice", "Violin", "Drums", "Cello", "Bass Guitar", "Flute", "Saxophone", "Trumpet", "Ukulele"];
export const QUICK_INSTRUMENTS = COMMON_INSTRUMENTS.filter((name) => INSTRUMENTS.includes(name));

const dedupe = (list) => [...new Set(list.map((item) => (item || "").trim()).filter(Boolean))];

const OnboardingPage = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // `/onboarding` has no route guard and AuthProvider fills the context inside
  // an EFFECT, so `user` is null on the first paint — including the paint right
  // after enrolment hands one over. Reading the handed-over user is what stops
  // the greeting rewriting itself and the rail springing forward a frame
  // later. Context stays the source of truth, so a refresh still resolves.
  const account = user ?? location.state?.user;

  // A founding member's rail continues from enrolment rather than restarting;
  // read from the account, never derived — see lib/founding.
  const founding = isFoundingMember(account);
  // Somebody who signed up FROM an invitation finishes on it. Sign-in leaves
  // the invitation stashed for a new account, because AuthProvider sends that
  // account here first whatever URL it was given (see lib/pendingInvite); this
  // is the first moment its page can actually be reached. It wins over the
  // founding welcome too, which stays one `?welcome=1` away (see Dashboard).
  const invitation = inviteDestination(peekInvite(), account);

  const [instruments, setInstruments] = useState([]);
  const [instrumentInput, setInstrumentInput] = useState("");
  const [band, setBand] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const firstName = (account?.first_name || account?.name?.split(" ")[0] || "there").trim() || "there";
  // What the tag field shows: anything typed rather than tapped. A quick
  // instrument typed into it lights its chip instead.
  const typedExtras = instruments.filter((name) => !QUICK_INSTRUMENTS.includes(name));

  const toggleChip = (name) => {
    setInstruments((list) => (list.includes(name) ? list.filter((item) => item !== name) : [...list, name]));
  };

  const setTyped = (extras) => {
    setInstruments((list) => dedupe([...list.filter((name) => QUICK_INSTRUMENTS.includes(name)), ...extras]));
  };

  const finish = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/auth/onboarding", {
        // Text left in the field without Enter still counts.
        instruments_taught: dedupe([...instruments, instrumentInput]),
        // Omitted entirely when unanswered — the server rejects an unknown
        // band, and "" is not one of them. Ignored for everyone outside the
        // cohort, which the server enforces rather than trusting this.
        ...(band ? { founding_student_band: band } : {}),
      });
      setUser(res.data);
      // An invitation spends its stash here and lands on it. Everyone else
      // lands on the dashboard; the welcome opens OVER it. The router state
      // is the trigger, and Dashboard clears it on read.
      if (invitation) {
        consumeInvite();
        navigate(invitation, { replace: true });
      } else {
        navigate("/dashboard", { replace: true, state: { welcome: true } });
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const chipClass = (pressed) =>
    pressed
      ? "rounded-full border border-[var(--accent-yellow)] bg-[var(--accent-yellow)] px-4 py-2 text-sm font-medium text-[var(--text-inverse)]"
      : "rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-4 py-2 text-sm text-[var(--text-primary)] hover:border-[var(--text-tertiary)]";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 overflow-hidden studio-spotlight">
      {/* The rail continues from enrolment for a founding teacher, who has
          already walked two screens to get here: this is the last of three.
          Everyone else has one screen, and a bar for one screen says nothing. */}
      {founding && (
        <div className="fixed top-0 left-0 right-0 h-1 bg-[var(--studio-interactive)] z-50">
          <motion.div
            className="h-full bg-[var(--accent-yellow)]"
            animate={{ width: `${((FOUNDING_STEPS + 1) / JOURNEY_STEPS) * 100}%` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            data-testid="onboarding-progress"
            data-step={FOUNDING_STEPS + 1}
            data-total={JOURNEY_STEPS}
          />
        </div>
      )}

      {/* Logo */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
        <span
          className="text-lg font-semibold tracking-tight text-[var(--text-primary)]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Hans
        </span>
      </div>

      <motion.div variants={fadeUp} initial="enter" animate="center" className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1
            className="text-2xl md:text-3xl font-light text-[var(--text-primary)]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {founding ? "Tell us what you teach" : `Welcome, ${firstName}`}
          </h1>
          <p className="text-[var(--text-secondary)] text-sm mt-2">
            {founding ? "One question, then you're in." : "One question, then you're teaching."}
          </p>
        </div>

        <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-6 space-y-5">
          <div>
            <span id="onboarding-instruments-label" className="block text-[var(--text-secondary)] text-sm font-medium mb-3">
              What do you teach <span className="text-[var(--text-quaternary)] text-xs">(optional)</span>
            </span>
            <div className="flex flex-wrap gap-2" role="group" aria-labelledby="onboarding-instruments-label" data-testid="instrument-chips">
              {QUICK_INSTRUMENTS.map((name) => {
                const pressed = instruments.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleChip(name)}
                    aria-pressed={pressed}
                    className={chipClass(pressed)}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            <label htmlFor="onboarding-other" className="mt-4 block text-[var(--text-secondary)] text-sm font-medium mb-2">
              Something else? Type it
            </label>
            <TagInput
              value={typedExtras}
              onInputChange={setInstrumentInput}
              onChange={setTyped}
              suggestions={INSTRUMENTS}
              placeholder="e.g. Ukulele, Music theory"
            />
            <p className="mt-3 text-xs text-[var(--text-tertiary)]">
              Optional. Pick any that fit or type your own; add more any time in Settings › Profile.
            </p>
          </div>

          {/* How many students — founding cohort only. Asked here rather than
              at enrolment, so the sign-up is not held hostage to a research
              question. Tapping the chosen band again clears it. */}
          {founding && (
            <div className="pt-1 border-t border-[var(--studio-divider)]" data-testid="founding-band-field">
              <label className="mb-2 mt-4 block text-sm font-medium text-[var(--text-secondary)]">
                How many students do you teach? <span className="text-[var(--text-quaternary)] text-xs">(optional)</span>
              </label>
              <div className="grid grid-cols-4 gap-2" data-testid="founding-band">
                {STUDENT_BANDS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setBand((current) => (current === option ? "" : option))}
                    aria-pressed={band === option}
                    className={chipClass(band === option)}
                  >
                    {BAND_LABELS[option]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Button
            onClick={finish}
            disabled={submitting}
            className="w-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full px-8 py-3 text-sm
                       hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start teaching"}
            {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <p className="mt-4 text-center text-xs text-[var(--text-tertiary)]">
            Nothing here is required. Where you teach, your timezone and phone can be set any time in Settings › Profile.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingPage;
